import { useState, useRef } from 'react'
import { useTheme } from '@emotion/react'
import { useAnimation } from 'framer-motion'

import { useInterval } from 'react-use'

import useMatchMedia from '@segment/utils/useMatchMedia'

import type { Card } from './Hero'

interface useMobileListCarouselProps {
  data: Card[]
}

export const useMobileListCarousel = ({ data }: useMobileListCarouselProps) => {
  interface wrapProps {
    min: number
    max: number
    value: number
  }

  const position = ({ min, max, value }: wrapProps): number => {
    const rangeSize = max - min
    return ((((value - min) % rangeSize) + rangeSize) % rangeSize) + min
  }

  const swipePower = (offset: number, velocity: number): number => {
    return (offset / velocity) * 100
  }

  const theme = useTheme()
  const animation = useAnimation()

  const ref = useRef<HTMLDivElement>(null)
  const [page, setPage] = useState<number>(0)
  const [drag, setDrag] = useState<boolean>(false)

  const largeCardResolutionMediaQuery = theme.breakpoints.up(`sm`, false)
  const isLargeCardResolution = useMatchMedia(largeCardResolutionMediaQuery, true)
  const isEnabledMediaQuery = theme.breakpoints.down(`sm`, false)
  const isEnabled = useMatchMedia(isEnabledMediaQuery, true)

  const prevGhost = position({ min: 0, max: data.length, value: page - 1 })
  const prev = position({ min: 0, max: data.length, value: page })
  const current = position({ min: 0, max: data.length, value: page + 1 })
  const next = position({ min: 0, max: data.length, value: page + 2 })
  const nextGhost = position({ min: 0, max: data.length, value: page + 3 })

  const handleDragEnd = async (_e: any, { offset }: any) => {
    const power = swipePower(offset.x, ref.current?.getBoundingClientRect().width ?? 0)

    if (power > 60) {
      await animation.start(`toRight`)
      paginate(-1)
    } else if (power < -60) {
      await animation.start(`toLeft`)
      paginate(1)
    }

    setDrag(false)
  }

  const handleDragStart = () => {
    setDrag(true)
  }

  useInterval(
    async () => {
      await animation.start(`toLeft`)
      paginate(1)
    },
    !isEnabled || drag ? null : 3000
  )

  const paginate = (dir: number) => {
    setPage(page + dir)
  }

  return {
    handleDragStart,
    handleDragEnd,
    isLargeCardResolution,
    items: {
      prevGhost,
      prev,
      current,
      next,
      nextGhost,
    },
    ref,
    animation,
    page,
  }
}
